import React from "react";
import { useLang } from "@/lib/i18n";
import { MessageCircle, Share2 } from "lucide-react";

// Returns a humanized time difference (e.g. "23 minutes", "4 hours", "2 days").
function humanizeDuration(fromIso, toIso, t) {
  if (!fromIso || !toIso) return null;
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  const mins = Math.round((to - from) / 60000);
  if (mins < 60) return `${Math.max(1, mins)} ${t("minutes")}`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} ${t("hours")}`;
  const days = Math.round(hours / 24);
  return `${days} ${t("days_")}`;
}

/**
 * One-tap WhatsApp share for accepted orders.
 * Shown on the customer's request detail page once an offer is accepted.
 *
 * Props:
 *  - category: category key (e.g. "cakes")
 *  - createdAt: request.created_at ISO
 *  - acceptedAt: order.created_at ISO (preferred) or offer.created_at fallback
 */
export default function ShareWin({ category, createdAt, acceptedAt }) {
  const { t, tCat } = useLang();
  const time = humanizeDuration(createdAt, acceptedAt, t);
  if (!time) return null; // hide if we cannot reliably compute the speed brag

  const url = (typeof window !== "undefined" && window.location?.origin) || "https://hustlersksa.com";
  const message = t("shareWaMsg")
    .replace("{cat}", tCat(category))
    .replace("{time}", time)
    .replace("{url}", url);

  const waHref = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <div className="mt-4 card-surface p-5 border-emerald-200 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white" data-testid="share-win-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-white/15 grid place-items-center shrink-0">
          <Share2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold leading-snug" data-testid="share-win-title">
            {t("shareWaTitle")}
          </div>
          <p className="text-xs opacity-90 mt-1 leading-relaxed">{t("shareWaSub")}</p>
        </div>
      </div>
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        data-testid="share-win-wa"
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white text-emerald-700 font-semibold px-4 py-3 hover:bg-emerald-50 transition"
      >
        <MessageCircle className="h-4 w-4" />
        {t("shareWaCta")}
      </a>
    </div>
  );
}
